import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '@/components/ui/toast';

import { DataTable, type DataTableColumn } from '@/components/shared/data-table';
import { PageHeader } from '@/components/shared/page-header';
import { Spinner } from '@/components/ui/spinner';
import { ApiError } from '@/lib/api-errors';
import { api } from '@/lib/api';
import {
  buildReceiptPdfBlob,
  type BillingDocumentPdfLabels,
  type BillingReceiptDocument,
} from '@/utils/billing-document-pdf';
import { formatDate, formatMoney } from '@/utils/format';
import { openBlobInNewTab } from '@/utils/open-blob-in-new-tab';

interface ReceiptRow {
  id: string;
  churchId: string;
  churchName?: string;
  receiptNumber: number;
  invoiceNumber: number;
  amountMinor: number;
  currency: string;
  issuedAt: string;
}

export const ReceiptsPage = () => {
  const { t } = useTranslation('admin');
  const { t: tErrors } = useTranslation('errors');
  const [openingDocumentId, setOpeningDocumentId] = useState<string | null>(null);

  const pdfLabels = useCallback(
    (document: BillingReceiptDocument): BillingDocumentPdfLabels => ({
      merchantName: t('billing.pdf.merchantName'),
      merchantDescriptor: t('billing.pdf.merchantDescriptor'),
      merchantCompany: t('billing.pdf.merchantCompany'),
      merchantContact: t('billing.pdf.merchantContact'),
      merchantAddress: t('billing.pdf.merchantAddress'),
      invoiceTitle: t('billing.pdf.invoiceTitle'),
      invoiceNumber: t('billing.pdf.invoiceNumber', { number: document.invoiceNumber }),
      billTo: t('billing.pdf.billTo'),
      issued: t('billing.pdf.issued'),
      due: t('billing.pdf.due'),
      paid: t('billing.pdf.paid'),
      status: t('billing.pdf.status'),
      period: t('billing.pdf.period'),
      description: t('billing.pdf.description'),
      seats: t('billing.pdf.seats'),
      amount: t('billing.pdf.amount'),
      planLine: '',
      seatsLine: '',
      coupon: '',
      subtotal: t('billing.pdf.subtotal'),
      discount: t('billing.pdf.discount'),
      tax: t('billing.pdf.tax'),
      total: t('billing.pdf.total'),
      amountPaid: t('billing.pdf.amountPaid'),
      amountDue: t('billing.pdf.amountDue'),
      receiptTitle: t('billing.pdf.receiptTitle'),
      receiptNumber: t('billing.pdf.receiptNumber', { number: document.receiptNumber }),
      receivedFrom: t('billing.pdf.receivedFrom'),
      invoiceLabel: t('billing.pdf.invoiceLabel'),
      invoiceRef: t('billing.pdf.invoiceRef', { number: document.invoiceNumber }),
      amountPaidLabel: t('billing.pdf.amountPaid'),
      subscriptionPayment: t('billing.pdf.subscriptionPayment'),
      thankYou: t('billing.pdf.thankYou'),
      statuses: {},
    }),
    [t],
  );

  const openReceiptPdf = useCallback(
    async (row: ReceiptRow) => {
      if (openingDocumentId) return;
      setOpeningDocumentId(row.id);
      try {
        const document = await api.get<BillingReceiptDocument>(`/platform/billing/receipts/${row.id}`);
        const blob = await buildReceiptPdfBlob(
          document,
          pdfLabels(document),
          (amountMinor, currency, locale) => formatMoney(amountMinor, currency, { locale }),
        );
        if (!openBlobInNewTab(blob)) {
          toast.error(t('billing.receipts.popupBlocked'));
        }
      } catch (err) {
        toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
      } finally {
        setOpeningDocumentId(null);
      }
    },
    [openingDocumentId, pdfLabels, t, tErrors],
  );

  const receipts = useQuery({
    queryKey: ['platform-receipts'],
    queryFn: () =>
      api.get<{ data: ReceiptRow[] }>('/platform/billing/receipts', {
        query: { page: 1, pageSize: 100 },
      }),
  });

  const columns: DataTableColumn<ReceiptRow>[] = [
    {
      id: 'issuedAt',
      header: t('billing.receipts.issuedAt'),
      cell: (row) => formatDate(row.issuedAt),
    },
    {
      id: 'receiptNumber',
      header: t('billing.receipts.receiptNumber'),
      cell: (row) => `#${row.receiptNumber}`,
    },
    {
      id: 'invoiceNumber',
      header: t('billing.receipts.invoiceNumber'),
      cell: (row) => `#${row.invoiceNumber}`,
    },
    {
      id: 'church',
      header: t('billing.receipts.church'),
      cell: (row) => row.churchName ?? row.churchId,
    },
    {
      id: 'amount',
      header: t('billing.receipts.amount'),
      cell: (row) => formatMoney(row.amountMinor, row.currency),
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader title={t('billing.receipts.title')} description={t('billing.receipts.description')} />

      <div className="relative" aria-busy={Boolean(openingDocumentId)}>
        <DataTable
          columns={columns}
          rows={receipts.data?.data ?? []}
          rowKey={(row) => row.id}
          loading={receipts.isLoading}
          onRowClick={(row) => void openReceiptPdf(row)}
          rowTooltip={() => t('billing.receipts.openPdf')}
          empty={t('billing.receipts.empty')}
        />
        {openingDocumentId ? (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-surface/70">
            <div className="flex items-center gap-2 text-body-sm text-fg-secondary">
              <Spinner className="size-5 text-brand" />
              <span>{t('billing.receipts.openingPdf')}</span>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
